import Papa from 'papaparse'
import { buildIdentityGroups } from '../../netlify/functions/_shared/prospecting-identity.mjs'
import { parseProspectingRows } from '../../netlify/functions/_shared/prospecting-records.mjs'
import { PROSPECTING_CONFIG } from '../config'
import type {
  PlatinumBillingKey,
  PlatinumPlan,
  ProspectingDataset,
  ProspectingListId,
  ProspectingRecord,
} from '../types/prospecting'

export type LoadProspectingDatasetOptions = { cacheBust?: boolean }

function exportUrl(gid: string, cacheBustToken?: string): string {
  const url = new URL(
    `https://docs.google.com/spreadsheets/d/${PROSPECTING_CONFIG.sheetId}/export`,
  )
  url.searchParams.set('format', 'csv')
  url.searchParams.set('gid', gid)
  if (cacheBustToken) url.searchParams.set('_prospecting_refresh', cacheBustToken)
  return url.toString()
}

async function fetchSheetRows(
  gid: string,
  signal?: AbortSignal,
  cacheBustToken?: string,
): Promise<string[][]> {
  const response = await fetch(exportUrl(gid, cacheBustToken), {
    method: 'GET',
    cache: 'no-store',
    signal,
  })

  if (!response.ok) throw new Error(`Google Sheets returned ${response.status}`)

  const csv = await response.text()
  const parsed = Papa.parse<string[]>(csv, { delimiter: ',', skipEmptyLines: false })

  if (parsed.errors.length > 0) {
    throw new Error(`Could not parse Prospecting sheet: ${parsed.errors[0].message}`)
  }

  return parsed.data
}

const clean = (value: unknown) => String(value ?? '').replace(/\r/g, '').trim()

// Prospect rows and their identity keys are parsed by the same modules the
// server-side Leads reconciliation uses, so a record the sync treats as an
// existing Lead is the same record this page labels "Also in Leads".
const LIST_ORDER: ProspectingListId[] = ['hot', 'audit', 'leads', 'old']

function money(token: string): number | undefined {
  const normalized = token.toLowerCase().replace(/[$,\s]/g, '')
  const match = normalized.match(/^(\d+(?:\.\d+)?)(k)?$/)
  if (!match) return undefined
  const amount = Number(match[1])
  if (!Number.isFinite(amount)) return undefined
  return match[2] ? Math.round(amount * 1000) : Math.round(amount)
}

function parsePrices(lines: string[]): Partial<Record<PlatinumBillingKey, number>> {
  const prices: Partial<Record<PlatinumBillingKey, number>> = {}

  for (const line of lines) {
    const match = line.match(/\$?([\d,.]+\s*k?)\s+per\s+(year|6\s*months?|quarter|month)/i)
    if (!match) continue
    const value = money(match[1])
    if (!value) continue

    const period = match[2].toLowerCase().replace(/\s+/g, ' ')
    if (period === 'year') prices.annual = value
    else if (period.startsWith('6')) prices.sixMonths = value
    else if (period === 'quarter') prices.quarterly = value
    else if (period === 'month') prices.monthly = value
  }

  return prices
}

function parsePlan(section: string, locations: number): PlatinumPlan | null {
  const lines = section.split(/\n+/).map((line) => line.trim()).filter(Boolean)
  if (!lines.length) return null

  return {
    id: `platinum-${locations}`,
    title: `Up to ${locations} locations`,
    locations,
    features: lines.filter(
      (line) =>
        !/^comes with/i.test(line) &&
        !/^up to\s+\d+\s+locations?/i.test(line) &&
        !/\$?[\d,.]+\s*k?\s+per\s+(year|6\s*months?|quarter|month)/i.test(line),
    ),
    prices: parsePrices(lines),
  }
}

function parsePlatinum(rows: string[][]): { plans: PlatinumPlan[]; raw: string } {
  const raw = rows.flat().map(clean).filter(Boolean).join('\n')
  if (!raw) return { plans: [], raw: '' }

  const firstMarker = raw.search(/up to\s+5\s+locations/i)
  const secondMarker = raw.search(/up to\s+10\s+locations/i)
  const plans: PlatinumPlan[] = []

  if (firstMarker >= 0) {
    const section = secondMarker > firstMarker ? raw.slice(firstMarker, secondMarker) : raw.slice(firstMarker)
    const plan = parsePlan(section, 5)
    if (plan) plans.push(plan)
  }

  if (secondMarker >= 0) {
    const plan = parsePlan(raw.slice(secondMarker), 10)
    if (plan) plans.push(plan)
  }

  return { plans, raw }
}

export async function loadProspectingDataset(
  signal?: AbortSignal,
  options: LoadProspectingDatasetOptions = {},
): Promise<ProspectingDataset> {
  const token = options.cacheBust ? String(Date.now()) : undefined

  const [hotRows, auditRows, leadRows, oldRows, platinumRows] = await Promise.all([
    fetchSheetRows(PROSPECTING_CONFIG.sheets.hot.gid, signal, token),
    fetchSheetRows(PROSPECTING_CONFIG.sheets.audit.gid, signal, token),
    fetchSheetRows(PROSPECTING_CONFIG.sheets.leads.gid, signal, token),
    fetchSheetRows(PROSPECTING_CONFIG.sheets.old.gid, signal, token),
    fetchSheetRows(PROSPECTING_CONFIG.sheets.platinum.gid, signal, token),
  ])

  const parsedRows: Record<ProspectingListId, string[][]> = {
    hot: hotRows,
    audit: auditRows,
    leads: leadRows,
    old: oldRows,
  }

  // One pass over every tab, so identities that only connect through another
  // tab's row (a shared phone here, a shared domain there) still collapse.
  const parsed = LIST_ORDER.flatMap((list) => parseProspectingRows(parsedRows[list], list))

  const lists: Record<ProspectingListId, ProspectingRecord[]> = { hot: [], audit: [], leads: [], old: [] }
  const memberships: Record<string, ProspectingListId[]> = {}

  for (const { record, groupId } of buildIdentityGroups(parsed)) {
    // A row with no usable identifier stands alone rather than merging with
    // every other unidentifiable row.
    const identityKey = groupId || `row:${record.list}:${record.rowNumber}`
    lists[record.list].push({
      ...record,
      identityKey,
      id: `${record.list}:${record.rowNumber}:${identityKey}`,
    })

    const current = memberships[identityKey] ?? []
    if (!current.includes(record.list)) current.push(record.list)
    memberships[identityKey] = current
  }

  const platinum = parsePlatinum(platinumRows)

  return {
    lists,
    memberships,
    platinum: platinum.plans,
    platinumRaw: platinum.raw,
    loadedAt: new Date(),
  }
}

